import { Link, router } from "@inertiajs/react";
import { ArrowLeft, RefreshCw, MapPin, FileDown } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import EmailComposer from "../../components/EmailComposer";
import FirmRelationshipCard, { FirmRelationship } from "../../components/FirmRelationshipCard";

interface JobDetail {
  id: number;
  title: string;
  firm_name: string;
  locations: string[];
  jd_year_range: string;
  job_type: string;
  practice_area: string;
  status: string;
  source: string;
  sourcing_status: string;
  non_recruiter: boolean;
  skip_reason: string | null;
  description: string | null;
  source_document_url: string | null;
  first_seen_at: string | null;
  created_at: string;
}

interface Props {
  job: JobDetail;
  ai_assist: boolean;
  firm_relationship: FirmRelationship | null;
}

const STATUS_BADGE: Record<string, string> = {
  pending_evaluation: "bg-yellow-100 text-yellow-700",
  worth_sourcing:     "bg-green-100 text-green-700",
  sourced:            "bg-blue-100 text-blue-700",
  skipped:            "bg-gray-100 text-gray-500",
  failed:             "bg-red-100 text-red-600",
};

const JOB_STATUS_BADGE: Record<string, string> = {
  open:           "bg-green-100 text-green-700",
  closed:         "bg-gray-100 text-gray-500",
  no_change:      "bg-yellow-100 text-yellow-700",
  direct_hire:    "bg-purple-100 text-purple-700",
  not_recruiting: "bg-red-100 text-red-600",
};

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

export default function Show({ job, ai_assist, firm_relationship }: Props) {
  function handleReevaluate() {
    router.post(`/jobs/${job.id}/reevaluate`);
  }

  const canReevaluate = job.sourcing_status === "skipped" || job.sourcing_status === "failed";

  return (
    <AppLayout title={job.title || job.firm_name}>
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Back */}
        <Link href="/jobs" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft size={14} />
          Jobs
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{job.firm_name}</h1>
              {job.title && <p className="text-sm text-gray-500 mt-0.5">{job.title}</p>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${JOB_STATUS_BADGE[job.status] ?? "bg-gray-100 text-gray-500"}`}>
                  {job.status.replace(/_/g, " ")}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[job.sourcing_status] ?? "bg-gray-100 text-gray-500"}`}>
                  {job.sourcing_status.replace(/_/g, " ")}
                </span>
              </div>
            </div>
            {canReevaluate && (
              <button
                onClick={handleReevaluate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors shrink-0"
              >
                <RefreshCw size={13} />
                Re-evaluate
              </button>
            )}
          </div>
        </div>

        {/* Firm relationship */}
        <FirmRelationshipCard relationship={firm_relationship} />

        {/* Skip reason */}
        {job.skip_reason && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            <span className="font-medium">Skipped:</span> {job.skip_reason}
          </div>
        )}

        {/* Details */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 grid grid-cols-2 gap-4">
          <Field label="Practice Area" value={job.practice_area} />
          <Field label="Job Type" value={job.job_type} />
          <Field label="JD Year Range" value={job.jd_year_range} />
          <Field label="Source" value={job.source?.replace(/_/g, " ")} />
          {job.locations?.length > 0 && (
            <div className="col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Locations</p>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <MapPin size={13} className="text-gray-400" />
                <span className="text-sm text-gray-900">{job.locations.join(", ")}</span>
              </div>
            </div>
          )}
          <div className="col-span-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Added</p>
            <p className="text-sm text-gray-900 mt-0.5">{new Date(job.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Source document download */}
        {job.source_document_url && (
          <a href={job.source_document_url}
            className="inline-flex items-center gap-1.5 text-sm text-green-700 border border-green-200 bg-white hover:bg-green-50 rounded-md px-3 py-1.5 transition-colors">
            <FileDown size={13} />
            Download source PDF
          </a>
        )}

        {/* Description */}
        {job.description && (
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Description</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{job.description}</p>
          </div>
        )}

        {/* Email Composer */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Draft Email</p>
          <EmailComposer
            templateTypes={["firm_outreach", "firm_skm", "submission"]}
            defaultType="firm_outreach"
            aiAssist={ai_assist}
            context={{
              job: {
                title:          job.title,
                firm_name:      job.firm_name,
                practice_area:  job.practice_area,
                location:       job.locations?.join(", ") ?? "",
                jd_year_range:  job.jd_year_range,
                description:    job.description,
              },
            }}
          />
        </div>
      </div>
    </AppLayout>
  );
}
